import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import ActionCreators from "../../actions";
import { setLyftReportData, setTuroReportData, setOtherReportData } from "../../utils/functions";
import { getCertificateFontDownloads } from "../../utils/certificateFontDownloads";
import "./certificateDownloadButton.css";
import moment from "moment";
import {
  PDFDownloadLink,
  Page,
  Text,
  View,
  Image,
  Document,
  StyleSheet,
  Font,
  Svg,
  Path,
} from "@react-pdf/renderer";

import lyftCarImage from "../../Assets/lyftCertificateForNevadaCarsPics.png";
import kalamFont from "../../font/Kalam-Light.ttf";
import "../../font/registerPatrickHandAndCaveatPdf";
import {
  handDrawnCirclePath,
  HAND_DRAWN_CIRCLE_VIEWBOX,
} from "../../utils/handDrawnCircle";

Font.register({ family: "kalamFont", format: "truetype", src: kalamFont });

const LYFT_LOGO_URL =
  "https://res.cloudinary.com/dgjordf6e/image/upload/v1742279338/image_22_kh5uqw.png";

const inspectionColumns = [
  [
    { number: 1, label: "Foot brakes", aliases: ["foot brake", "foot brakes", "brake"] },
    {
      number: 2,
      label: "Emergency brake (parking brake)",
      aliases: ["emergency brake", "parking brake"],
    },
    { number: 3, label: "Steering mechanism", aliases: ["steering"] },
    {
      number: 4,
      label: "Windshield",
      aliases: ["windshield"],
      subChecks: [
        { label: "Large crack", aliases: ["large crack"] },
        { label: "Small crack", aliases: ["small crack"] },
      ],
    },
    {
      number: 5,
      label: "Rear window and other glass",
      aliases: ["rear window", "other glass"],
    },
    { number: 6, label: "Windshield wipers", aliases: ["wiper", "wipers"] },
    { number: 7, label: "Headlights", aliases: ["headlight", "headlights"] },
    { number: 8, label: "Tail lights", aliases: ["tail light", "tail lights"] },
    {
      number: 9,
      label: "Turn indicator lights",
      aliases: ["turn indicator", "turn signal", "indicator lights"],
    },
    { number: 10, label: "Stop lights", aliases: ["stop light", "brake light"] },
    {
      number: 11,
      label: "Front seat adjustment",
      aliases: ["front seat adjustment", "seat adjustment"],
    },
  ],
  [
    {
      number: 12,
      label: "Doors (open, close, lock)",
      aliases: ["door", "doors"],
      valueLabel: "Number of doors:",
      valueKey: "vehicleDoors",
    },
    { number: 13, label: "Horn", aliases: ["horn"] },
    { number: 14, label: "Speedometer", aliases: ["speedometer"] },
    { number: 15, label: "Bumpers", aliases: ["bumper", "bumpers"] },
    {
      number: 16,
      label: "Muffler and exhaust system",
      aliases: ["muffler", "exhaust"],
    },
    {
      number: 17,
      label: "Tires, incl. tread depth",
      aliases: ["tire", "tires", "tread depth"],
    },
    {
      number: 18,
      label: "Interior and exterior rear view mirrors",
      aliases: ["rear view mirror", "rearview mirror", "mirrors"],
    },
    {
      number: 19,
      label: "Safety belts for driver and passenger(s)",
      aliases: ["safety belt", "seat belt"],
      valueLabel: "Number of seatbelts:",
      valueKey: "seats",
    },
    {
      number: 20,
      label: "Tires, incl. tread depth",
      aliases: ["tire", "tires", "tread depth"],
    },
    {
      number: 21,
      label: "Interior and exterior rear view mirrors",
      aliases: ["rear view mirror", "rearview mirror", "mirrors"],
    },
    {
      number: 22,
      label: "Safety belts for driver and passenger(s)",
      aliases: ["safety belt", "seat belt"],
    },
  ],
];

const bodyDamageItem = { aliases: ["body damage", "damage"] };

const normalize = (value = "") =>
  String(value)
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const getFullName = (data = {}) =>
  `${data?.Username || ""} ${data?.UserlastName || ""}`.trim();

const getInspectorName = (data = {}) =>
  `${data?.Inspectorname || ""} ${data?.InspectorlastName || ""}`.trim();

const formatDate = (value) =>
  value ? moment(new Date(value)).format("MM/DD/YYYY") : "";

const getTncNumber = (data = {}) => {
  const plate = data?.licensePlateNumber || "";
  return plate ? plate.substring(Math.max(0, plate.length - 4)) : "";
};

const getCheckStatus = (checks = [], aliases = []) => {
  const normalizedAliases = aliases.map(normalize);
  const match = checks.find((check) => {
    const name = normalize(check?.Check?.name || check?.name);
    if (!name) return false;
    return normalizedAliases.some(
      (alias) => name.includes(alias) || alias.includes(name)
    );
  });

  if (!match) return null;
  const status = String(match.status).toLowerCase();
  return match.status === true ||
    match.status === 1 ||
    status === "true" ||
    status === "1" ||
    status === "pass"
    ? "pass"
    : "fail";
};

const styles = StyleSheet.create({
  page: {
    backgroundColor: "#fff",
    color: "#111",
    fontFamily: "Helvetica",
    paddingBottom: 0,
  },
  header: {
    alignItems: "center",
    backgroundColor: "#000",
    color: "#fff",
    flexDirection: "row",
    height: 76,
    justifyContent: "space-between",
    paddingHorizontal: 66,
  },
  brand: {
    alignItems: "center",
    flexDirection: "row",
  },
  logo: {
    height: 29,
    objectFit: "contain",
    width: 72,
  },
  divider: {
    backgroundColor: "#fff",
    height: 32,
    marginHorizontal: 22,
    width: 1,
  },
  stateText: {
    color: "#fff",
    fontSize: 21,
    fontWeight: 700,
  },
  headerTitle: {
    color: "#fff",
    fontSize: 21,
    fontWeight: 700,
  },
  driverSection: {
    paddingHorizontal: 66,
    paddingTop: 12,
  },
  titleRow: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  driverTitle: {
    fontSize: 22,
    fontWeight: 800,
  },
  mandatory: {
    fontSize: 12,
    fontWeight: 800,
  },
  row: {
    flexDirection: "row",
    marginBottom: 9,
  },
  field: {
    marginRight: 18,
  },
  fieldValue: {
    borderBottomColor: "#777",
    borderBottomWidth: 1.5,
    fontSize: 12.5,
    minHeight: 17,
    paddingBottom: 2,
  },
  fieldLabel: {
    fontSize: 10,
    marginTop: 3,
    textTransform: "uppercase",
  },
  wHalf: {
    flex: 1,
  },
  wPlate: {
    width: 410,
  },
  wVin: {
    flex: 1,
  },
  wMake: {
    width: 410,
  },
  wModel: {
    flex: 1,
  },
  wYear: {
    width: 150,
  },
  wSignature: {
    width: 410,
  },
  noMargin: {
    marginRight: 0,
  },
  vinBoxes: {
    borderBottomColor: "#777",
    borderBottomWidth: 1.5,
    flexDirection: "row",
    height: 21,
  },
  vinBox: {
    alignItems: "center",
    borderLeftColor: "#777",
    borderLeftWidth: 1.5,
    flex: 1,
    justifyContent: "center",
  },
  vinBoxLast: {
    borderRightColor: "#777",
    borderRightWidth: 1.5,
  },
  vinChar: {
    fontSize: 12.5,
  },
  inspectorLine: {
    borderBottomColor: "#777",
    borderBottomStyle: "dashed",
    borderBottomWidth: 1.5,
    marginTop: 1,
    position: "relative",
  },
  inspectorLineText: {
    alignSelf: "center",
    backgroundColor: "#fff",
    fontSize: 15,
    fontWeight: 800,
    marginBottom: -9,
    paddingHorizontal: 8,
  },
  checklist: {
    paddingHorizontal: 66,
    paddingTop: 23,
  },
  checklistTitle: {
    fontSize: 22,
    fontWeight: 800,
    marginBottom: 9,
  },
  checklistGrid: {
    flexDirection: "row",
  },
  checkColumn: {
    flex: 1,
  },
  leftColumn: {
    marginRight: 44,
  },
  checkHeading: {
    alignItems: "center",
    color: "#777",
    flexDirection: "row",
    fontSize: 12.5,
    marginBottom: 4,
    textTransform: "uppercase",
  },
  pointHeading: {
    flex: 1,
  },
  statusHeading: {
    textAlign: "center",
    width: 46,
  },
  checkRow: {
    alignItems: "center",
    flexDirection: "row",
    minHeight: 20,
  },
  checkLabel: {
    flex: 1,
    flexDirection: "row",
  },
  checkNumber: {
    color: "#777",
    fontSize: 12,
    textAlign: "right",
    width: 25,
  },
  checkText: {
    fontSize: 12.5,
    fontWeight: 800,
    lineHeight: 1.15,
  },
  subCheckText: {
    fontSize: 12.5,
    marginLeft: 25,
  },
  boxWrap: {
    alignItems: "center",
    width: 46,
  },
  bodyBoxWrap: {
    width: 62,
  },
  bodyReinspectBoxWrap: {
    width: 84,
  },
  box: {
    borderColor: "#777",
    borderWidth: 1.4,
    height: 13,
    position: "relative",
    width: 13,
  },
  tick: {
    height: 18,
    left: -2,
    position: "absolute",
    top: -7,
    width: 18,
  },
  checkValue: {
    flexDirection: "row",
    fontSize: 12.5,
    minHeight: 21,
    paddingLeft: 25,
  },
  checkValueLine: {
    borderBottomColor: "#777",
    borderBottomWidth: 1.3,
    marginLeft: 8,
    minHeight: 15,
    textAlign: "center",
    width: 70,
  },
  bodyFull: {
    marginTop: 18,
  },
  bodyRow: {
    flexDirection: "row",
    marginTop: 8,
  },
  bodyMain: {
    flex: 1.05,
    marginRight: 30,
  },
  bodySide: {
    flex: 0.95,
    paddingTop: 30,
  },
  bodyHeading: {
    alignItems: "center",
    color: "#555",
    flexDirection: "row",
    fontSize: 12.5,
    marginBottom: 5,
    textTransform: "uppercase",
  },
  bodyPointHeading: {
    flex: 1,
  },
  bodyStatusHeading: {
    textAlign: "center",
    width: 62,
  },
  bodyReinspectHeading: {
    width: 84,
  },
  bodyCheck: {
    alignItems: "center",
    flexDirection: "row",
  },
  bodyText: {
    flex: 1,
    flexDirection: "row",
    fontSize: 12.2,
  },
  carMap: {
    height: 150,
    marginLeft: -22,
    objectFit: "contain",
    width: 440,
  },
  resultBox: {
    borderColor: "#777",
    borderStyle: "dashed",
    borderWidth: 1.5,
    minHeight: 102,
    paddingHorizontal: 18,
    paddingTop: 12,
  },
  resultTop: {
    alignItems: "center",
    flexDirection: "row",
  },
  resultTitle: {
    flex: 1,
    fontSize: 13,
    fontWeight: 800,
  },
  resultCircleOne: {
    color: "#666",
    fontSize: 11.5,
    fontWeight: 400,
  },
  resultOption: {
    fontSize: 13.5,
    fontWeight: 800,
    position: "relative",
    textAlign: "center",
    width: 70,
  },
  selectedResult: {
    borderColor: "#111",
    borderWidth: 2,
    borderRadius: 28,
    paddingVertical: 5,
  },
  resultOptionWrap: {
    width: 70,
    height: 30,
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },
  resultOptionText: {
    fontSize: 13.5,
    fontWeight: 800,
    textAlign: "center",
  },
  resultCircle: {
    position: "absolute",
    width: 72,
    height: 44,
    left: -1,
    top: -7,
  },
  resultNote: {
    color: "#666",
    fontSize: 11.5,
    lineHeight: 1.2,
    marginTop: 6,
    textAlign: "center",
  },
  tnc: {
    alignSelf: "flex-end",
    borderColor: "#777",
    borderStyle: "dashed",
    borderWidth: 1.5,
    marginTop: 8,
    padding: 6,
    width: 190,
  },
  tncText: {
    borderBottomColor: "#777",
    borderBottomWidth: 1.5,
    fontSize: 13.5,
    fontWeight: 800,
  },
  tncSub: {
    fontSize: 9.5,
    fontWeight: 700,
    marginTop: 4,
  },
  inspectorSection: {
    backgroundColor: "#e8e3e3",
    marginHorizontal: 50,
    marginTop: 5,
    paddingHorizontal: 16,
    paddingBottom: 14,
    paddingTop: 14,
  },
  inspectorTitle: {
    fontSize: 15,
    fontWeight: 800,
    marginBottom: 16,
  },
  signatureImage: {
    height: 36,
    objectFit: "contain",
    width: 170,
  },
});

const Field = ({ label, value, style, handwritingStyle }) => (
  <View style={[styles.field, style].filter(Boolean)}>
    <Text style={[styles.fieldValue, handwritingStyle].filter(Boolean)}>
      {value || ""}
    </Text>
    <Text style={styles.fieldLabel}>{label}</Text>
  </View>
);

const VinField = ({ value, handwritingStyle }) => {
  const chars = String(value || "").slice(0, 17).split("");
  return (
    <View style={[styles.field, styles.wVin, styles.noMargin].filter(Boolean)}>
      <View style={styles.vinBoxes}>
        {Array.from({ length: 17 }).map((_, index) => (
          <View
            style={[
              styles.vinBox,
              index === 16 && styles.vinBoxLast,
            ].filter(Boolean)}
            key={index}
          >
            <Text style={[styles.vinChar, handwritingStyle].filter(Boolean)}>
              {chars[index] || ""}
            </Text>
          </View>
        ))}
      </View>
      <Text style={styles.fieldLabel}>VIN</Text>
    </View>
  );
};

const CheckBox = ({ checked, wrapStyle }) => (
  <View style={[styles.boxWrap, wrapStyle].filter(Boolean)}>
    <View style={styles.box}>
      {checked ? (
        <Svg style={styles.tick} viewBox="0 0 24 24">
          <Path
            d="M4 12.5C6.3 15.4 8.1 17.7 9.2 18.7C12.5 13.5 16.1 8.8 21 4.4"
            stroke="#111"
            strokeWidth={2.7}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </Svg>
      ) : null}
    </View>
  </View>
);

// Renders a PASS/FAIL option with a hand-drawn (human-like) circle around it
// when selected, matching the other certificates.
const ResultOption = ({ selected, children }) => (
  <View style={styles.resultOptionWrap}>
    {selected ? (
      <Svg style={styles.resultCircle} viewBox={HAND_DRAWN_CIRCLE_VIEWBOX}>
        <Path
          d={handDrawnCirclePath}
          stroke="#111"
          strokeWidth={3}
          strokeLinecap="round"
          fill="none"
        />
      </Svg>
    ) : null}
    <Text style={styles.resultOptionText}>{children}</Text>
  </View>
);

const InspectionItem = ({ item, checks, data }) => {
  const status = getCheckStatus(checks, item.aliases);
  return (
    <>
      <View style={styles.checkRow}>
        <View style={styles.checkLabel}>
          <Text style={styles.checkNumber}>{item.number}. </Text>
          <Text style={styles.checkText}>{item.label}</Text>
        </View>
        <CheckBox checked={status === "pass"} />
        <CheckBox checked={status === "fail"} />
      </View>
      {item.subChecks?.map((subCheck) => {
        const subStatus = "pass";
        return (
          <View style={styles.checkRow} key={subCheck.label}>
            <View style={styles.checkLabel}>
              <Text style={styles.subCheckText}>{subCheck.label}</Text>
            </View>
            <CheckBox checked={subStatus === "pass"} />
            <CheckBox checked={subStatus === "fail"} />
          </View>
        );
      })}
      {item.valueLabel ? (
        <View style={styles.checkValue}>
          <Text>{item.valueLabel}</Text>
          <Text style={styles.checkValueLine}>
            {data?.[item.valueKey] || (item.valueKey === "vehicleDoors" ? "4" : item.valueKey === "seats" ? "5" : "")}
          </Text>
        </View>
      ) : null}
    </>
  );
};

function LyftCertificateNevadaDownLoad({
  vehicleChecksArray,
  certificateData = {},
  handwritingFont = "kalamFont",
  fileName = "Vehicle-certificate.pdf",
  linkLabel = "Download pdf",
}) {
  const handwritingStyle = { fontFamily: handwritingFont };
  const finalStatus = String(certificateData?.finalStatus || "").toLowerCase();
  const bodyStatus = getCheckStatus(vehicleChecksArray, bodyDamageItem.aliases);

  return (
    <div className="App">
      <PDFDownloadLink
        document={
          <Document>
            <Page size="A3" style={styles.page} wrap={false}>
              <View style={styles.header}>
                <View style={styles.brand}>
                  <Image style={styles.logo} source={`${LYFT_LOGO_URL}?x-request=xhr`} />
                  <View style={styles.divider} />
                  <Text style={styles.stateText}>NEVADA</Text>
                </View>
                <Text style={styles.headerTitle}>VEHICLE INSPECTION</Text>
              </View>

              <View style={styles.driverSection}>
                <View style={styles.titleRow}>
                  <Text style={styles.driverTitle}>LYFT DRIVER INFO</Text>
                  <Text style={styles.mandatory}>ALL FIELDS ARE MANDATORY</Text>
                </View>
                <View style={styles.row}>
                  <Field
                    label="LYFT DRIVER NAME"
                    value={getFullName(certificateData)}
                    style={styles.wHalf}
                    handwritingStyle={handwritingStyle}
                  />
                  <Field
                    label="EMAIL ADDRESS"
                    value={certificateData?.Useremail}
                    style={[styles.wHalf, styles.noMargin].filter(Boolean)}
                    handwritingStyle={handwritingStyle}
                  />
                </View>
                <View style={styles.row}>
                  <Field
                    label="LICENSE PLATE #"
                    value={certificateData?.licensePlateNumber}
                    style={styles.wPlate}
                    handwritingStyle={handwritingStyle}
                  />
                  <VinField
                    value={certificateData?.vin}
                    handwritingStyle={handwritingStyle}
                  />
                </View>
                <View style={styles.row}>
                  <Field
                    label="VEHICLE MAKE"
                    value={certificateData?.make}
                    style={styles.wMake}
                    handwritingStyle={handwritingStyle}
                  />
                  <Field
                    label="VEHICLE MODEL"
                    value={certificateData?.model}
                    style={styles.wModel}
                    handwritingStyle={handwritingStyle}
                  />
                  <Field
                    label="VEHICLE YEAR"
                    value={certificateData?.year}
                    style={[styles.wYear, styles.noMargin].filter(Boolean)}
                    handwritingStyle={handwritingStyle}
                  />
                </View>
                <View style={styles.row}>
                  <Field
                    label="LYFT DRIVER SIGNATURE"
                    style={styles.wSignature}
                    handwritingStyle={handwritingStyle}
                  />
                  <Field
                    label="LYFT DRIVER PHONE NUMBER"
                    value={certificateData?.Userphone}
                    style={[styles.wHalf, styles.noMargin].filter(Boolean)}
                    handwritingStyle={handwritingStyle}
                  />
                </View>
              </View>

              <View style={styles.inspectorLine}>
                <Text style={styles.inspectorLineText}>
                  INSPECTOR ONLY BELOW THIS LINE
                </Text>
              </View>

              <View style={styles.checklist}>
                <Text style={styles.checklistTitle}>
                  VEHICLE INSPECTION CHECKLIST
                </Text>
                <View style={styles.checklistGrid}>
                  {inspectionColumns.map((column, columnIndex) => (
                    <View
                      style={
                        columnIndex === 0
                          ? [styles.checkColumn, styles.leftColumn]
                          : styles.checkColumn
                      }
                      key={columnIndex}
                    >
                      <View style={styles.checkHeading}>
                        <Text style={styles.pointHeading}>INSPECTION POINT</Text>
                        <Text style={styles.statusHeading}>PASS</Text>
                        <Text style={styles.statusHeading}>FAIL</Text>
                      </View>
                      {column.map((item) => (
                        <InspectionItem
                          item={item}
                          checks={vehicleChecksArray}
                          data={certificateData}
                          key={item.number}
                        />
                      ))}
                    </View>
                  ))}
                </View>

                <View style={styles.bodyFull}>
                    <View style={styles.bodyHeading}>
                      <Text style={styles.bodyPointHeading}>INSPECTION POINT</Text>
                      <Text style={styles.bodyStatusHeading}>PASS</Text>
                      <Text style={styles.bodyStatusHeading}>FAIL</Text>
                      <Text style={styles.bodyStatusHeading}>ADDING?</Text>
                      <Text
                        style={[
                          styles.bodyStatusHeading,
                          styles.bodyReinspectHeading,
                        ].filter(Boolean)}
                      >
                        REINSPECT?
                      </Text>
                    </View>
                    <View style={styles.bodyCheck}>
                      <View style={styles.bodyText}>
                        <Text style={styles.checkNumber}>23. </Text>
                        <Text style={styles.checkText}>Body damage </Text>
                        <Text>
                          (If fail, please mark with an X where the damage is on
                          the vehicle.)
                        </Text>
                      </View>
                      <CheckBox checked={bodyStatus === "pass"} wrapStyle={styles.bodyBoxWrap} />
                      <CheckBox checked={bodyStatus === "fail"} wrapStyle={styles.bodyBoxWrap} />
                      <CheckBox wrapStyle={styles.bodyReinspectBoxWrap} />
                      <CheckBox wrapStyle={styles.bodyBoxWrap} />
                    </View>
                </View>

                <View style={styles.bodyRow}>
                  <View style={styles.bodyMain}>
                    <Image style={styles.carMap} src={lyftCarImage} />
                  </View>

                  <View style={styles.bodySide}>
                    <View style={styles.resultBox}>
                      <View style={styles.resultTop}>
                        <Text style={styles.resultTitle}>
                          VEHICLE INSPECTION{" "}
                          <Text style={styles.resultCircleOne}>(Circle One)</Text>
                        </Text>
                        <ResultOption selected={finalStatus === "pass"}>
                          PASS
                        </ResultOption>
                        <ResultOption selected={finalStatus === "fail"}>
                          FAIL
                        </ResultOption>
                      </View>
                      <Text style={styles.resultNote}>
                        (One failed check mark makes for an overall failed Vehicle
                        Inspection)
                      </Text>
                      <Text style={styles.resultNote}>
                        Applicant - Please note that a failed vehicle inspection
                        form will be reviewed before a final decision is made.
                      </Text>
                    </View>
                    <View style={styles.tnc}>
                      <Text style={styles.tncText}>
                        TNC: 0001-{getTncNumber(certificateData)}
                      </Text>
                      <Text style={styles.tncSub}>
                        Add last 4 digits of the License Plate #
                      </Text>
                    </View>
                  </View>
                </View>
              </View>

              <View style={styles.inspectorSection}>
                <Text style={styles.inspectorTitle}>
                  TO BE COMPLETED BY INSPECTOR
                </Text>
                <View style={styles.row}>
                  <Field
                    label="COMPANY NAME"
                    value="Chex Inc."
                    style={styles.wHalf}
                    handwritingStyle={handwritingStyle}
                  />
                  <Field
                    label="INSPECTOR NAME"
                    value={getInspectorName(certificateData)}
                    style={[styles.wHalf, styles.noMargin].filter(Boolean)}
                    handwritingStyle={handwritingStyle}
                  />
                </View>
                <View style={styles.row}>
                  <Field
                    label="COMPANY/FACILITY ADDRESS"
                    value={certificateData?.stateAddress}
                    style={styles.wHalf}
                    handwritingStyle={handwritingStyle}
                  />
                  <Field
                    label="INSPECTION DATE (document expires one year from this date)"
                    value={formatDate(certificateData?.reviewedDate)}
                    style={[styles.wHalf, styles.noMargin].filter(Boolean)}
                    handwritingStyle={handwritingStyle}
                  />
                </View>
                <View style={[styles.field, styles.noMargin].filter(Boolean)}>
                  <View style={styles.fieldValue}>
                    {certificateData?.Inspectorsignature ? (
                      <Image
                        style={styles.signatureImage}
                        source={`${process.env.REACT_APP_AWS_S3_LINK}/${certificateData.Inspectorsignature}?x-request=xhr`}
                      />
                    ) : null}
                  </View>
                  <Text style={styles.fieldLabel}>INSPECTOR SIGNATURE</Text>
                </View>
              </View>
            </Page>
          </Document>
        }
        fileName={fileName}
      >
        {({ loading }) => (
          <>
            {loading ? (
              <div className="certificate-download-button is-loading" aria-live="polite">
                <span className="certificate-download-spinner" />
                <div>Preparing PDF</div>
              </div>
            ) : (
              <div className="certificate-download-button">
                <div>{linkLabel}</div>
                <i className="fa fa-download" />
              </div>
            )}
          </>
        )}
      </PDFDownloadLink>
    </div>
  );
}


const LyftCertificateNevadaDownLoadConnected = (props) => {
  const { id, companyId, reportData, checksData } = props;
  React.useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      props.getVehicleCertificate({ id, companyId }, props.setLoading, "template3");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);
  return (
    <div className="certificate-download-group">
      {!reportData ? (
        <div className="certificate-download-button is-loading" aria-live="polite">
          <span className="certificate-download-spinner" />
          <div>Loading certificate data</div>
        </div>
      ) : (
        getCertificateFontDownloads(reportData?.certificateFont).map((cfg) => (
          <LyftCertificateNevadaDownLoad
            key={cfg.handwritingFont}
            reportData={reportData} certificateData={reportData}
            checksData={checksData} checks={checksData} vehicleChecksArray={checksData} firstHalfData={checksData}
            handwritingFont={cfg.handwritingFont}
            fileName={cfg.fileName}
            linkLabel={cfg.linkLabel}
          />
        ))
      )}
    </div>
  );
};

function mapStateToProps(state) {
  const sliceData = state && state.vehicleInstruction && state.vehicleInstruction.lyftVehicleCertificate;
  return {
    reportData: sliceData,
    checksData: setLyftReportData(sliceData && sliceData.Files),
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(ActionCreators, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(LyftCertificateNevadaDownLoadConnected);
